import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Badge, Button, Card, DeviceLink, Input, Spinner } from '@/components';
import type { AdminCrossword, CrosswordSolvesResponse } from '@/lib/admin-types';
import { apiJson } from '@/lib/api';
import { formatAdminDateTime, formatCrosswordClue, formatMinutesSeconds, countFallbackVariantClues, isFallbackVariantClue } from '@/lib/format';

export const Route = createFileRoute('/admin/crosswords/$id')({
  component: CrosswordDetailPage,
});

function CrosswordDetailPage() {
  const { id } = Route.useParams();
  const puzzleId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const detailQ = useQuery({
    queryKey: ['admin', 'crossword', puzzleId],
    queryFn: () => apiJson<AdminCrossword>(`/v1/admin/crosswords/${puzzleId}`),
    enabled: Number.isFinite(puzzleId),
  });
  const solvesQ = useQuery({
    queryKey: ['admin', 'crossword', puzzleId, 'solves'],
    queryFn: () =>
      apiJson<CrosswordSolvesResponse>(`/v1/admin/crosswords/${puzzleId}/solves`),
    enabled: Number.isFinite(puzzleId),
  });

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiJson<AdminCrossword>(`/v1/admin/crosswords/${puzzleId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'crossword', puzzleId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'crosswords'] });
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: () =>
      apiJson<AdminCrossword>(`/v1/admin/crosswords/${puzzleId}/regenerate`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'crossword', puzzleId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'crosswords'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiJson<void>(`/v1/admin/crosswords/${puzzleId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'crosswords'] });
      void navigate({ to: '/admin/crosswords' });
    },
  });

  if (detailQ.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-600">{(detailQ.error as Error)?.message ?? 'Not found'}</p>
        <Button variant="secondary" onClick={() => void navigate({ to: '/admin/crosswords' })}>
          ← All crosswords
        </Button>
      </div>
    );
  }

  return (
    <CrosswordEditor
      puzzle={detailQ.data}
      solves={solvesQ.data ?? null}
      loadingSolves={solvesQ.isLoading}
      saving={saveMutation.isPending}
      regenerating={regenerateMutation.isPending}
      deleting={deleteMutation.isPending}
      onSave={(body) => saveMutation.mutate(body)}
      onRegenerate={() => regenerateMutation.mutate()}
      onDelete={() => deleteMutation.mutate()}
      onBack={() => void navigate({ to: '/admin/crosswords' })}
    />
  );
}

type CrosswordEditorProps = {
  puzzle: AdminCrossword;
  solves: CrosswordSolvesResponse | null;
  loadingSolves: boolean;
  saving: boolean;
  regenerating: boolean;
  deleting: boolean;
  onSave: (body: Record<string, unknown>) => void;
  onRegenerate: () => void;
  onDelete: () => void;
  onBack: () => void;
};

type CrosswordWorkflow = 'draft' | 'queued';
type ClueRow = AdminCrossword['clues']['across'][number];

function workflowFromPuzzle(puzzle: AdminCrossword): CrosswordWorkflow {
  return puzzle.is_published && !puzzle.served_on_date ? 'queued' : 'draft';
}

function CrosswordEditor({
  puzzle,
  solves,
  loadingSolves,
  saving,
  regenerating,
  deleting,
  onSave,
  onRegenerate,
  onDelete,
  onBack,
}: CrosswordEditorProps) {
  const [title, setTitle] = useState(puzzle.title);
  const [subtitle, setSubtitle] = useState(puzzle.subtitle ?? '');
  const [workflow, setWorkflow] = useState<CrosswordWorkflow>(() => workflowFromPuzzle(puzzle));
  const [acrossClues, setAcrossClues] = useState<ClueRow[]>(
    puzzle.clues.across.map((clue) => ({ ...clue }))
  );
  const [downClues, setDownClues] = useState<ClueRow[]>(
    puzzle.clues.down.map((clue) => ({ ...clue }))
  );

  const isServed = Boolean(puzzle.served_on_date);

  const updateClueText = (
    direction: 'across' | 'down',
    clueId: string,
    clueText: string
  ) => {
    const setter = direction === 'across' ? setAcrossClues : setDownClues;
    setter((prev) =>
      prev.map((row) => (row.id === clueId ? { ...row, clue: clueText } : row))
    );
  };

  useEffect(() => {
    setTitle(puzzle.title);
    setSubtitle(puzzle.subtitle ?? '');
    setWorkflow(workflowFromPuzzle(puzzle));
    setAcrossClues(puzzle.clues.across.map((clue) => ({ ...clue })));
    setDownClues(puzzle.clues.down.map((clue) => ({ ...clue })));
  }, [puzzle]);

  const needsReviewCount = countFallbackVariantClues({
    across: acrossClues,
    down: downClues,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Button variant="secondary" onClick={onBack}>
            ← All crosswords
          </Button>
          <h1 className="mt-3 text-2xl font-semibold">
            {puzzle.served_on_date
              ? `Served on ${puzzle.served_on_date}`
              : puzzle.queue_order != null
                ? `Queued #${puzzle.queue_order}`
                : 'Draft Candidate'}
          </h1>
          <p className="text-sm text-gray-500">{puzzle.title}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={isServed || workflow === 'queued' ? 'success' : 'neutral'}>
            {isServed ? 'Served' : workflow === 'queued' ? 'Queued' : 'Draft'}
          </Badge>
          {needsReviewCount > 0 ? (
            <Badge variant="warning">
              {needsReviewCount} clue{needsReviewCount === 1 ? '' : 's'} need rewrite
            </Badge>
          ) : null}
        </div>
      </div>

      <Card className="space-y-4">
        <h2 className="text-sm font-medium text-gray-900">Puzzle settings</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-gray-600">Title</span>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600">Subtitle</span>
            <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className="mt-1" />
          </label>
        </div>
        <label className="block text-sm">
          <span className="text-gray-600">Status</span>
          {isServed ? (
            <p className="mt-1 text-sm text-gray-600">
              Served on{' '}
              <span className="font-medium text-gray-900">{puzzle.served_on_date}</span> — cannot be
              changed.
            </p>
          ) : (
            <>
              <select
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
                value={workflow}
                onChange={(e) => setWorkflow(e.target.value as CrosswordWorkflow)}
              >
                <option value="draft">Draft — review and edit before publishing</option>
                <option value="queued">Queued — next in line for daily serving</option>
              </select>
              <p className="mt-2 text-xs text-gray-500">
                {workflow === 'queued'
                  ? puzzle.queue_order != null
                    ? `Position #${puzzle.queue_order} in the queue. The app serves the next queued puzzle automatically each day.`
                    : 'This puzzle will join the end of the queue when you save.'
                  : 'Draft puzzles are not served. Move to Queued when ready for the daily rotation.'}
              </p>
            </>
          )}
        </label>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            disabled={saving}
            loading={saving}
            onClick={() =>
              onSave({
                title: title.trim(),
                subtitle: subtitle.trim() || null,
                ...(isServed ? {} : { is_published: workflow === 'queued' }),
              })
            }
          >
            Save metadata
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={regenerating}
            loading={regenerating}
            onClick={onRegenerate}
          >
            Regenerate clues/grid
          </Button>
          <Button
            type="button"
            variant="danger"
            size="sm"
            disabled={deleting}
            loading={deleting}
            onClick={() => {
              if (window.confirm('Delete this crossword? This cannot be undone.')) {
                onDelete();
              }
            }}
          >
            Delete
          </Button>
        </div>
      </Card>

      <Card className="space-y-3">
        <h2 className="text-sm font-medium text-gray-900">Solved puzzle preview</h2>
        <p className="text-xs text-gray-500">
          This is the literal crossword layout with answers filled in, exactly how clue
          intersections line up.
        </p>
        <SolvedGridPreview
          puzzle={puzzle}
          acrossClues={acrossClues}
          downClues={downClues}
        />
      </Card>

      <Card className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h2 className="text-sm font-medium text-gray-900">Edit clues</h2>
          {needsReviewCount > 0 ? (
            <Badge variant="warning">{needsReviewCount} flagged</Badge>
          ) : null}
        </div>
        {needsReviewCount > 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
            <p className="font-medium">Clues ending in (2), (3), … need a manual rewrite.</p>
            <p className="mt-1 text-amber-900/80">
              The generator could not produce a fresh wording and fell back to a numbered
              duplicate of the English gloss. Players would see that suffix unless you edit
              these clues before publishing.
            </p>
          </div>
        ) : null}
        <p className="text-xs text-gray-500">
          Refine clue text directly. Answers, numbering, and grid coordinates stay unchanged.
        </p>
        <ClueEditorSection
          title="Across"
          clues={acrossClues}
          onChange={(clueId, clueText) => updateClueText('across', clueId, clueText)}
        />
        <ClueEditorSection
          title="Down"
          clues={downClues}
          onChange={(clueId, clueText) => updateClueText('down', clueId, clueText)}
        />
        <Button
          type="button"
          size="sm"
          onClick={() => {
            onSave({
              clues: {
                across: acrossClues.map((row) => ({ ...row, clue: row.clue.trim() })),
                down: downClues.map((row) => ({ ...row, clue: row.clue.trim() })),
              },
            });
          }}
          disabled={saving}
          loading={saving}
        >
          Save clues
        </Button>
        <p className="text-xs text-gray-500">
          Tip: flagged clues use auto-generated fallback text. Rewrite them here before
          publishing — the manual editor is not checked against clue history.
        </p>
      </Card>

      <Card className="space-y-3">
        <h2 className="text-sm font-medium text-gray-900">Solve times</h2>
        {loadingSolves ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : solves ? (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs text-gray-500">Total solves</p>
                <p className="mt-1 text-xl font-semibold text-gray-900">{solves.count}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs text-gray-500">Fastest</p>
                <p className="mt-1 text-xl font-semibold text-gray-900">
                  {formatMinutesSeconds(solves.fastest_seconds)}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs text-gray-500">Average</p>
                <p className="mt-1 text-xl font-semibold text-gray-900">
                  {formatMinutesSeconds(solves.average_seconds)}
                </p>
              </div>
            </div>

            {solves.solves.length === 0 ? (
              <p className="text-xs text-gray-500">No solved runs yet.</p>
            ) : (
              <div className="overflow-auto rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Rank</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Player</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Time</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Played</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {solves.solves.map((solve) => (
                      <tr key={`${solve.rank}-${solve.installation_id}-${solve.played_at}`}>
                        <td className="px-3 py-2 text-gray-900">#{solve.rank}</td>
                        <td className="px-3 py-2 text-gray-900">
                          <DeviceLink
                            installationId={solve.installation_id}
                            nickname={solve.nickname}
                          />
                        </td>
                        <td className="px-3 py-2 text-gray-900">
                          {formatMinutesSeconds(solve.duration_seconds)}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {formatAdminDateTime(solve.played_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <p className="text-xs text-gray-500">Could not load solve times.</p>
        )}
      </Card>
    </div>
  );
}

function ClueEditorSection({
  title,
  clues,
  onChange,
}: {
  title: string;
  clues: ClueRow[];
  onChange: (clueId: string, clueText: string) => void;
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {clues.length === 0 ? (
        <p className="text-xs text-gray-500">No {title.toLowerCase()} clues.</p>
      ) : (
        <div className="space-y-2">
          {clues
            .slice()
            .sort((a, b) => a.number - b.number)
            .map((row) => {
              const flagged = isFallbackVariantClue(row.clue);
              return (
              <label
                key={row.id}
                className={`block rounded-xl text-sm ${
                  flagged ? 'border border-amber-300 bg-amber-50/70 p-3' : ''
                }`}
              >
                <span className="flex flex-wrap items-center gap-2 text-gray-600">
                  <span>
                    {row.number}. {row.answer} ({row.length})
                  </span>
                  {flagged ? (
                    <Badge variant="warning">Needs rewrite</Badge>
                  ) : null}
                </span>
                <Input
                  className={`mt-1 ${flagged ? 'border-amber-300 bg-white' : ''}`}
                  value={row.clue}
                  onChange={(e) => onChange(row.id, e.target.value)}
                />
              </label>
            );
            })}
        </div>
      )}
    </div>
  );
}

function SolvedGridPreview({
  puzzle,
  acrossClues,
  downClues,
}: {
  puzzle: AdminCrossword;
  acrossClues: ClueRow[];
  downClues: ClueRow[];
}) {
  const rows = Math.max(1, Number(puzzle.grid?.rows ?? 0));
  const cols = Math.max(1, Number(puzzle.grid?.cols ?? 0));
  const cells = Array.isArray(puzzle.grid?.cells) ? puzzle.grid.cells : [];
  const cellMap = new Map<string, { solution: string; number?: number | null }>();
  for (const cell of cells) {
    cellMap.set(`${cell.row}:${cell.col}`, {
      solution: String(cell.solution ?? "").toUpperCase().slice(0, 1),
      number: cell.number ?? null,
    });
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
      <div className="overflow-auto rounded-xl border border-gray-200 bg-gray-50 p-3">
        <div
          className="inline-grid gap-[1px] bg-gray-300 p-[1px]"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 36px))` }}
        >
          {Array.from({ length: rows }).flatMap((_, r) =>
            Array.from({ length: cols }).map((__, c) => {
              const key = `${r}:${c}`;
              const info = cellMap.get(key);
              if (!info) {
                return (
                  <div
                    key={key}
                    className="h-9 w-9 bg-gray-900"
                    title={`Block (${r}, ${c})`}
                  />
                );
              }
              return (
                <div
                  key={key}
                  className="relative flex h-9 w-9 items-center justify-center bg-white font-mono text-sm font-semibold text-gray-900"
                  title={`Cell (${r}, ${c}) = ${info.solution}`}
                >
                  {info.number != null ? (
                    <span className="absolute left-0.5 top-0.5 text-[9px] font-medium text-gray-500">
                      {info.number}
                    </span>
                  ) : null}
                  {info.solution}
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-1">
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-900">Across</h3>
            {acrossClues.length === 0 ? (
              <p className="text-xs text-gray-500">No across clues</p>
            ) : (
              <ul className="space-y-1.5">
                {acrossClues
                  .slice()
                  .sort((a, b) => a.number - b.number)
                  .map((clue) => {
                    const flagged = isFallbackVariantClue(clue.clue);
                    return (
                    <li
                      key={clue.id}
                      className={`text-xs ${
                        flagged
                          ? 'rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-amber-950'
                          : 'text-gray-700'
                      }`}
                    >
                      <span className="font-semibold text-gray-900">
                        {clue.number}.
                      </span>{" "}
                      {formatCrosswordClue(clue.clue)}
                      {flagged ? (
                        <span className="ml-1 font-medium text-amber-800">· needs rewrite</span>
                      ) : null}
                    </li>
                  );
                  })}
              </ul>
            )}
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-900">Down</h3>
            {downClues.length === 0 ? (
              <p className="text-xs text-gray-500">No down clues</p>
            ) : (
              <ul className="space-y-1.5">
                {downClues
                  .slice()
                  .sort((a, b) => a.number - b.number)
                  .map((clue) => {
                    const flagged = isFallbackVariantClue(clue.clue);
                    return (
                    <li
                      key={clue.id}
                      className={`text-xs ${
                        flagged
                          ? 'rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-amber-950'
                          : 'text-gray-700'
                      }`}
                    >
                      <span className="font-semibold text-gray-900">
                        {clue.number}.
                      </span>{" "}
                      {formatCrosswordClue(clue.clue)}
                      {flagged ? (
                        <span className="ml-1 font-medium text-amber-800">· needs rewrite</span>
                      ) : null}
                    </li>
                  );
                  })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
